import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from '../ui/use-toast';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/authContext';

// Import the working getCurrentUserOrganizationId from data.js
import { getCurrentUserOrganizationId } from '../../lib/data.js';

const DemoSystemSettings = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isMaintaining, setIsMaintaining] = useState(false);
  const [demoStats, setDemoStats] = useState(null);
  const [lastMaintenance, setLastMaintenance] = useState(null);
  const [autoSchedule, setAutoSchedule] = useState(false);
  const [organizationId, setOrganizationId] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    const initializeComponent = async () => {
      const orgId = await getCurrentUserOrganizationId();
      console.log('🔍 [DemoSystem] Initializing with organization ID:', orgId);
      
      if (orgId) {
        setOrganizationId(orgId);
        loadDemoStats(orgId);
        checkLastMaintenance(orgId);
      } else {
        console.error('No organization ID found');
      }
    };
    
    initializeComponent();
  }, []);

  const loadDemoStats = async (orgId = organizationId) => {
    if (!orgId) {
      console.error('No organization ID provided for loadDemoStats');
      return;
    }
    

    
    try {
      const { data: members } = await supabase
        .from('members')
        .select('id, status, created_at')
        .eq('organization_id', orgId);

      const { data: events } = await supabase
        .from('events')
        .select('id, event_type, start_date')
        .eq('organization_id', orgId);

      const { data: attendance } = await supabase
        .from('event_attendance')
        .select('id, created_at')
        .eq('organization_id', orgId);

      const { data: donations } = await supabase
        .from('donations')
        .select('id, amount, date')
        .eq('organization_id', orgId);

      setDemoStats({
        members: members?.length || 0,
        visitors: members?.filter(m => m.status === 'visitor').length || 0,
        events: events?.length || 0,
        attendance: attendance?.length || 0,
        donations: donations?.length || 0,
        totalDonations: donations?.reduce((sum, d) => sum + (d.amount || 0), 0) || 0
      });
    } catch (error) {
      console.error('Failed to load demo stats:', error);
    }
  };

  const checkLastMaintenance = async (orgId = organizationId) => {
    if (!orgId) {
      console.error('No organization ID provided for checkLastMaintenance');
      return;
    }
    
    try {
      // Check for recent demo-generated data
      const { data: recentEvents } = await supabase
        .from('events')
        .select('created_at')
        .eq('organization_id', orgId)
        .ilike('description', '%demo data%')
        .order('created_at', { ascending: false })
        .limit(1);

      if (recentEvents && recentEvents.length > 0) {
        setLastMaintenance(new Date(recentEvents[0].created_at));
      }
    } catch (error) {
      console.error('Failed to check last maintenance:', error);
    }
  };

  const generateInitialDemoData = async () => {
    if (!organizationId) {
      toast({
        title: 'Error',
        description: 'Organization ID not found',
        variant: 'destructive',
      });
      return;
    }

    console.log('🔍 [DemoSystem] Generating demo data for organization:', organizationId);
    
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-demo-data', {
        body: {
          organizationId: organizationId,
          memberCount: 100,
          weeksToGenerate: 26 // 6 months
        }
      });

      if (error) throw error;

      console.log('✅ [DemoSystem] Demo data generated successfully:', data);

      toast({
        title: 'Success!',
        description: `Generated ${data.stats.members} members, ${data.stats.events} events, ${data.stats.attendance} attendance records, and ${data.stats.donations} donations.`,
      });

      await loadDemoStats();
      await checkLastMaintenance();
    } catch (error) {
      console.error('Demo generation failed:', error);
      toast({
        title: 'Generation Failed',
        description: error.message || 'Failed to generate demo data',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const runWeeklyMaintenance = async () => {
    if (!organizationId) {
      toast({
        title: 'Error',
        description: 'Organization ID not found',
        variant: 'destructive',
      });
      return;
    }

    setIsMaintaining(true);
    try {
      const { data, error } = await supabase.functions.invoke('weekly-demo-maintenance', {
        body: {
          organizationId: organizationId
        }
      });

      if (error) throw error;

      toast({
        title: 'Maintenance Complete!',
        description: `Added ${data.results.newEvents} events, ${data.results.attendanceAdded} attendance records, ${data.results.donationsAdded} donations, and ${data.results.newVisitors} new visitors.`,
      });

      await loadDemoStats();
      await checkLastMaintenance();
    } catch (error) {
      console.error('Weekly maintenance failed:', error);
      toast({
        title: 'Maintenance Failed',
        description: error.message || 'Failed to run weekly maintenance',
        variant: 'destructive',
      });
    } finally {
      setIsMaintaining(false);
    }
  };

  const resetDemoData = async () => {
    if (!organizationId) {
      toast({
        title: 'Error',
        description: 'Organization ID not found. Cannot safely reset data.',
        variant: 'destructive',
      });
      return;
    }

    const confirmMessage = `⚠️ SAFETY CHECK ⚠️

This will ONLY delete data for your organization (${organizationId}).

Data to be deleted:
- Members with organization_id matching yours
- Events with organization_id matching yours  
- Donations, tasks, groups for your organization only
- SMS templates that start with "Demo"
- Email messages, campaigns, templates, conversations for your organization
- Child check-ins/guardians linked to your members only

This will NOT affect:
- Data from other organizations
- SMS templates not starting with "Demo"
- Email data from other organizations
- Any data without proper organization isolation

Are you sure you want to proceed?`;

    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      console.log(`🔒 SAFE RESET: Starting demo data reset for organization: ${organizationId}`);
      
      // Get list of member IDs for this organization to safely delete related data
      const { data: orgMembers, error: membersFetchError } = await supabase
        .from('members')
        .select('id')
        .eq('organization_id', organizationId);
      
      if (membersFetchError) {
        throw new Error(`Failed to fetch organization members: ${membersFetchError.message}`);
      }
      
      const memberIds = orgMembers?.map(m => m.id) || [];
      console.log(`Found ${memberIds.length} members for organization ${organizationId}`);
      
      // Delete data in the correct order to avoid foreign key constraints
      // ONLY delete data that is confirmed to belong to this organization
      
      // 1. Delete SMS messages for this organization only
      const { count: smsMessagesDeleted } = await supabase
        .from('sms_messages')
        .delete()
        .eq('organization_id', organizationId)
        .select('*', { count: 'exact', head: true });
      
      console.log(`✅ Deleted ${smsMessagesDeleted || 0} SMS messages for organization ${organizationId}`);
      
      // 2. Delete SMS conversations for this organization only
      const { count: smsConversationsDeleted } = await supabase
        .from('sms_conversations')
        .delete()
        .eq('organization_id', organizationId)
        .select('*', { count: 'exact', head: true });
      
      console.log(`✅ Deleted ${smsConversationsDeleted || 0} SMS conversations for organization ${organizationId}`);
      
      // 3. Delete ONLY demo SMS templates (safe - only demo templates)
      const { count: smsTemplatesDeleted } = await supabase
        .from('sms_templates')
        .delete()
        .like('name', 'Demo%')
        .select('*', { count: 'exact', head: true });
      
      console.log(`✅ Deleted ${smsTemplatesDeleted || 0} demo SMS templates`);
      
      // 3a. Delete email messages for this organization
      const { count: emailMessagesDeleted } = await supabase
        .from('email_messages')
        .delete()
        .eq('organization_id', organizationId)
        .select('*', { count: 'exact', head: true });
      
      console.log(`✅ Deleted ${emailMessagesDeleted || 0} email messages for organization ${organizationId}`);
      
      // 3b. Delete email campaigns for this organization
      const { count: emailCampaignsDeleted } = await supabase
        .from('email_campaigns')
        .delete()
        .eq('organization_id', organizationId)
        .select('*', { count: 'exact', head: true });
      
      console.log(`✅ Deleted ${emailCampaignsDeleted || 0} email campaigns for organization ${organizationId}`);
      
      // 3c. Delete email templates for this organization
      const { count: emailTemplatesDeleted } = await supabase
        .from('email_templates')
        .delete()
        .eq('organization_id', organizationId)
        .select('*', { count: 'exact', head: true });
      
      console.log(`✅ Deleted ${emailTemplatesDeleted || 0} email templates for organization ${organizationId}`);
      
      // 3d. Delete email conversations for this organization
      const { count: emailConversationsDeleted } = await supabase
        .from('email_conversations')
        .delete()
        .eq('organization_id', organizationId)
        .select('*', { count: 'exact', head: true });
      
      console.log(`✅ Deleted ${emailConversationsDeleted || 0} email conversations for organization ${organizationId}`);
      
      // 4. Delete child check-in logs ONLY for members of this organization
      let childCheckInsDeleted = 0;
      if (memberIds.length > 0) {
        const { count } = await supabase
          .from('child_checkin_logs')
          .delete()
          .in('child_id', memberIds)
          .select('*', { count: 'exact', head: true });
        childCheckInsDeleted = count || 0;
      }
      console.log(`✅ Deleted ${childCheckInsDeleted} child check-ins for organization members`);
      
      // 5. Delete child guardians ONLY for members of this organization
      let childGuardiansDeleted = 0;
      if (memberIds.length > 0) {
        const { count } = await supabase
          .from('child_guardians')
          .delete()
          .in('child_id', memberIds)
          .select('*', { count: 'exact', head: true });
        childGuardiansDeleted = count || 0;
      }
      console.log(`✅ Deleted ${childGuardiansDeleted} child guardian relationships for organization members`);
      
      // 6. Delete task comments for this organization
      const { count: taskCommentsDeleted } = await supabase
        .from('task_comments')
        .delete()
        .eq('organization_id', organizationId)
        .select('*', { count: 'exact', head: true });
      
      console.log(`✅ Deleted ${taskCommentsDeleted || 0} task comments for organization ${organizationId}`);
      
      // 7. Delete tasks for this organization
      const { count: tasksDeleted } = await supabase
        .from('tasks')
        .delete()
        .eq('organization_id', organizationId)
        .select('*', { count: 'exact', head: true });
      
      console.log(`✅ Deleted ${tasksDeleted || 0} tasks for organization ${organizationId}`);
      
      // 8. Delete group members for this organization
      const { count: groupMembersDeleted } = await supabase
        .from('group_members')
        .delete()
        .eq('organization_id', organizationId)
        .select('*', { count: 'exact', head: true });
      
      console.log(`✅ Deleted ${groupMembersDeleted || 0} group members for organization ${organizationId}`);
      
      // 9. Delete groups for this organization
      const { count: groupsDeleted } = await supabase
        .from('groups')
        .delete()
        .eq('organization_id', organizationId)
        .select('*', { count: 'exact', head: true });
      
      console.log(`✅ Deleted ${groupsDeleted || 0} groups for organization ${organizationId}`);
      
      // 10. Delete event attendance for this organization
      const { count: attendanceDeleted } = await supabase
        .from('event_attendance')
        .delete()
        .eq('organization_id', organizationId)
        .select('*', { count: 'exact', head: true });
      
      console.log(`✅ Deleted ${attendanceDeleted || 0} attendance records for organization ${organizationId}`);
      
      // 11. Delete donations for this organization
      const { count: donationsDeleted } = await supabase
        .from('donations')
        .delete()
        .eq('organization_id', organizationId)
        .select('*', { count: 'exact', head: true });
      
      console.log(`✅ Deleted ${donationsDeleted || 0} donations for organization ${organizationId}`);
      
      // 12. Delete donation batches for this organization
      const { count: batchesDeleted } = await supabase
        .from('donation_batches')
        .delete()
        .eq('organization_id', organizationId)
        .select('*', { count: 'exact', head: true });
      
      console.log(`✅ Deleted ${batchesDeleted || 0} donation batches for organization ${organizationId}`);
      
      // 13. Delete events for this organization
      const { count: eventsDeleted } = await supabase
        .from('events')
        .delete()
        .eq('organization_id', organizationId)
        .select('*', { count: 'exact', head: true });
      
      console.log(`✅ Deleted ${eventsDeleted || 0} events for organization ${organizationId}`);
      
      // 14. Get families that have members from this organization
      let familyIds = [];
      if (memberIds.length > 0) {
        const { data: familiesWithOrgMembers } = await supabase
          .from('members')
          .select('family_id')
          .eq('organization_id', organizationId)
          .not('family_id', 'is', null);
        
        familyIds = [...new Set(familiesWithOrgMembers?.map(m => m.family_id).filter(Boolean) || [])];
      }
      
      // 15. Clear ALL family references BEFORE deleting anything
      // First clear family_id references in members for this organization
      await supabase
        .from('members')
        .update({ family_id: null })
        .eq('organization_id', organizationId);
      
      console.log(`✅ Cleared family_id references for organization ${organizationId} members`);
      
      // Clear primary_contact_id references in families (this is what was causing the constraint violation)
      if (familyIds.length > 0) {
        await supabase
          .from('families')
          .update({ primary_contact_id: null })
          .in('id', familyIds);
        
        console.log(`✅ Cleared primary_contact_id references in ${familyIds.length} families`);
      }
      
      // Also clear any primary_contact_id that might reference our organization's members
      if (memberIds.length > 0) {
        await supabase
          .from('families')
          .update({ primary_contact_id: null })
          .in('primary_contact_id', memberIds);
        
        console.log(`✅ Cleared primary_contact_id references pointing to organization ${organizationId} members`);
      }
      
      // 16. Now safely delete members for this organization
      const { count: membersDeleted } = await supabase
        .from('members')
        .delete()
        .eq('organization_id', organizationId)
        .select('*', { count: 'exact', head: true });
      
      console.log(`✅ Deleted ${membersDeleted || 0} members for organization ${organizationId}`);
      
      // 17. Finally delete families ONLY if they were associated with this organization's members
      let familiesDeleted = 0;
      if (familyIds.length > 0) {
        const { count } = await supabase
          .from('families')
          .delete()
          .in('id', familyIds)
          .select('*', { count: 'exact', head: true });
        
        familiesDeleted = count || 0;
      }
      console.log(`✅ Deleted ${familiesDeleted} families associated with organization ${organizationId}`);

      console.log(`🎉 SAFE RESET COMPLETE: All demo data for organization ${organizationId} has been safely removed`);

      toast({
        title: 'Demo Data Reset Complete',
        description: `Safely deleted all demo data for your organization. ${membersDeleted} members, ${eventsDeleted} events, ${donationsDeleted} donations, and related data removed.`,
      });

      setDemoStats({
        members: 0,
        visitors: 0,
        events: 0,
        attendance: 0,
        donations: 0,
        totalDonations: 0
      });
      setLastMaintenance(null);
    } catch (error) {
      console.error('❌ SAFE RESET FAILED:', error);
      toast({
        title: 'Reset Failed',
        description: `Failed to reset demo data: ${error.message}. No data was deleted.`,
        variant: 'destructive',
      });
    }
  };

  const toggleAutoSchedule = () => {
    setAutoSchedule(!autoSchedule);
    toast({
      title: autoSchedule ? 'Auto-Schedule Disabled' : 'Auto-Schedule Enabled',
      description: autoSchedule 
        ? 'Weekly maintenance will no longer run automatically' 
        : 'Weekly maintenance will run automatically every Sunday at 6 AM',
    });
  };

  const formatDate = (date) => {
    return date ? new Date(date).toLocaleDateString() + ' at ' + new Date(date).toLocaleTimeString() : 'Never';
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>🤖 Automated Demo System</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-gray-600">
            Generate and maintain realistic demo data automatically. Perfect for presentations, 
            testing, and showcasing your church management features.
          </div>
          
          {/* Demo Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{demoStats?.members || 0}</div>
              <div className="text-sm text-blue-800">Members</div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{demoStats?.visitors || 0}</div>
              <div className="text-sm text-green-800">Visitors</div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{demoStats?.events || 0}</div>
              <div className="text-sm text-purple-800">Events</div>
            </div>
            <div className="bg-yellow-50 p-3 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{demoStats?.attendance || 0}</div>
              <div className="text-sm text-yellow-800">Attendance</div>
            </div>
            <div className="bg-red-50 p-3 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{demoStats?.donations || 0}</div>
              <div className="text-sm text-red-800">Donations</div>
            </div>
            <div className="bg-indigo-50 p-3 rounded-lg">
              <div className="text-2xl font-bold text-indigo-600">${demoStats?.totalDonations || 0}</div>
              <div className="text-sm text-indigo-800">Total Raised</div>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Status:</span>
              <Badge variant={demoStats?.members > 0 ? 'default' : 'secondary'}>
                {demoStats?.members > 0 ? 'Active' : 'Not Set Up'}
              </Badge>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Auto-Schedule:</span>
              <Badge variant={autoSchedule ? 'default' : 'secondary'}>
                {autoSchedule ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
          </div>

          <div className="text-sm text-gray-600">
            Last Maintenance: {formatDate(lastMaintenance)}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={generateInitialDemoData}
              disabled={isGenerating}
              className="flex-1 min-w-[200px]"
            >
              {isGenerating ? 'Generating...' : '🎯 Generate Initial Demo Data'}
            </Button>
            
            <Button 
              onClick={runWeeklyMaintenance}
              disabled={isMaintaining}
              variant="outline"
              className="flex-1 min-w-[200px]"
            >
              {isMaintaining ? 'Running...' : '🔄 Run Weekly Maintenance'}
            </Button>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={toggleAutoSchedule}
              variant={autoSchedule ? 'destructive' : 'default'}
              className="flex-1 min-w-[200px]"
            >
              {autoSchedule ? '⏸️ Disable Auto-Schedule' : '⏰ Enable Auto-Schedule'}
            </Button>
            
            <Button 
              onClick={resetDemoData}
              variant="destructive"
              className="flex-1 min-w-[200px]"
            >
              🗑️ Safe Reset Demo Data
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>🔒 Data Safety Guarantee</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <span className="text-green-500">🛡️</span>
              <span className="text-sm"><strong>Organization Isolation:</strong> Only deletes data for your specific organization</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-green-500">🛡️</span>
              <span className="text-sm"><strong>Safe SMS Templates:</strong> Only removes templates starting with "Demo"</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-green-500">🛡️</span>
              <span className="text-sm"><strong>Linked Data Only:</strong> Child records only deleted if linked to your members</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-green-500">🛡️</span>
              <span className="text-sm"><strong>No Cross-Contamination:</strong> Other organizations' data is never affected</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-green-500">🛡️</span>
              <span className="text-sm"><strong>Detailed Logging:</strong> Every deletion is logged with counts for verification</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>📊 Demo Data Features</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <span className="text-green-500">✅</span>
              <span className="text-sm">Realistic member profiles with varied attendance patterns</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-green-500">✅</span>
              <span className="text-sm">Recurring events (Sunday services, Bible studies, fellowship)</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-green-500">✅</span>
              <span className="text-sm">Seasonal attendance variations (lower summer, higher winter)</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-green-500">✅</span>
              <span className="text-sm">Weekly donation patterns with realistic amounts</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-green-500">✅</span>
              <span className="text-sm">Automatic new visitor additions</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-green-500">✅</span>
              <span className="text-sm">Data cleanup to prevent database bloat</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-green-500">✅</span>
              <span className="text-sm">Self-maintaining - generates new data weekly</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>⚙️ How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm text-gray-600">
            <div>
              <strong>Initial Setup:</strong> Generate 6 months of historical data including members, 
              events, attendance, and donations with realistic patterns.
            </div>
            <div>
              <strong>Weekly Maintenance:</strong> Every week, the system automatically:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Creates upcoming events for the next month</li>
                <li>Adds realistic attendance to recent events</li>
                <li>Generates weekly donation records</li>
                <li>Adds 1-3 new visitors</li>
                <li>Cleans up old data (older than 1 year)</li>
              </ul>
            </div>
            <div>
              <strong>Realistic Patterns:</strong> The system uses algorithms to create believable 
              church data with seasonal variations, member behavior patterns, and appropriate 
              donation amounts.
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DemoSystemSettings; 